import React, { useEffect, useState } from 'react';
import Card from '../../Card';
import Button from '../../Button';
import Drawer from '../../Drawer';
import ConfirmDialog from '../../ConfirmDialog';
import { contactCardName } from '../../../domain/people';
import { formatAddress } from '../../../domain/sendingIdentity';
import {
  ACCESS, ACCESS_ACTIONS, familyPortalAccessActions, familyPortalAccessId, familyPortalAccessStatus,
  familyPortalEmailKey, familyPortalInviteFrom, familyPortalInviteSubject, familyPortalInviteText, familyPortalOpen,
  familyPortalSetupPlaceholder, familyPortalWord as w,
} from '../../../domain/familyPortal';
import { inviteFamilyContact, revokeFamilyContact, watchPortalAccess } from '../../../data/familyPortal';
import s from './FamilyPortalTab.module.css';

// The Family Portal tab (#770): MMS's Student Portal tab on the student, one
// row per contact of the student's family, each with its access and what can
// be done about it. Drawn only while the Family Portal is open (#769,
// StudentDetailPage.jsx).
//
// What MMS does and where we differ — no student rows, four states rather
// than Disabled/Enabled, a Re-send button — is written once beside the rules,
// at "Invitations" in core/src/family_portal.rs.
//
// Enable Access opens the preview first, as MMS's does: From, To, Subject and
// the message, with the link shown as MMS shows it, `<< Password Setup URL >>`,
// and Cancel and Send. Send is the callable (`familyPortalInvite`): the server
// writes the access record and queues the invitation in the studio's outbox,
// under the studio's name and sender, within the day's send budget, listed on
// Message History. Online only, and said so when it fails.
//
// From is the studio's name and address as the Email & SMS tab holds them
// (`invite_from` in core/src/family_portal.rs, shared with the phones' previews,
// #771). What the envelope carries is the sender's to decide (`envelopeFor` in
// src/domain/sendingIdentity.js), as for every other studio email.
//
// A family that deleted its own login (#774) reads "Login deleted", with a line
// saying the studio's records are unchanged and one button, Invite Again, which
// opens the same preview: the server moves the record there when the login
// goes (functions/deletion/store.js), and an invitation takes any status but
// active.
//
// The statuses are the server's, read from `users/{uid}/portalAccess`
// (src/data/familyPortal.js); nothing here writes them.

/**
 * The student page's tabs with this one appended — last, where MMS has its
 * Student Portal tab — while the Family Portal is open (#769), and unchanged
 * while it is closed. Appended, so every other tab keeps its index.
 */
export function withFamilyPortalTab(tabs) {
  return familyPortalOpen() ? [...tabs, w('tabName')] : tabs;
}

function StatusPill({ status }) {
  const label = {
    [ACCESS.notInvited]: w('statusNotInvited'),
    [ACCESS.invited]: w('statusInvited'),
    [ACCESS.active]: w('statusActive'),
    [ACCESS.revoked]: w('statusRevoked'),
    [ACCESS.loginDeleted]: w('statusLoginDeleted'),
  }[status];
  return <span className={[s.pill, s[status]].join(' ')}>{label}</span>;
}

const ACTION_LABEL = {
  [ACCESS_ACTIONS.enable]: 'actionEnable',
  [ACCESS_ACTIONS.resend]: 'actionResend',
  [ACCESS_ACTIONS.revoke]: 'actionRevoke',
  [ACCESS_ACTIONS.inviteAgain]: 'actionInviteAgain',
};

// The preview's title, by the button that opened it.
const PREVIEW_TITLE = {
  [ACCESS_ACTIONS.resend]: 'resendTitle',
  [ACCESS_ACTIONS.inviteAgain]: 'inviteAgainTitle',
};

const firstWord = (v) => (typeof v === 'string' && v.trim() ? v.trim().split(/\s+/)[0] : null);

export default function FamilyPortalTab({
  uid, student, family, contacts = [], contactsLoading = false,
  studioName = null, studioEmail = null, teacherFirstName = null,
}) {
  const familyId = family?.id || student?.familyId || null;
  const [access, setAccess] = useState({ familyId: null, rows: null, failed: false });
  const [preview, setPreview] = useState(null);   // { contact, action }
  const [revoking, setRevoking] = useState(null); // contact
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);       // { contactId, text } | { preview: text }

  useEffect(() => {
    if (!uid || !familyId) return undefined;
    return watchPortalAccess(
      uid,
      familyId,
      (rows) => setAccess({ familyId, rows, failed: false }),
      () => setAccess({ familyId, rows: [], failed: true }),
    );
  }, [uid, familyId]);

  if (!familyId) {
    return <Card title={w('tabName')}><div className={s.help}>This student is not attached to a family account.</div></Card>;
  }

  const rows = access.familyId === familyId ? access.rows : null;
  const byId = new Map((rows || []).map((r) => [r.id, r]));
  const accessOf = (contact) => byId.get(familyPortalAccessId(familyId, contact.id)) || null;

  const send = () => {
    const { contact } = preview;
    setBusy(true);
    setError(null);
    inviteFamilyContact({ familyId, contactId: contact.id }).then(
      () => { setBusy(false); setPreview(null); },
      (err) => { setBusy(false); setError({ preview: err.message }); },
    );
  };

  const revoke = () => {
    const contact = revoking;
    setRevoking(null);
    setError(null);
    revokeFamilyContact({ familyId, contactId: contact.id }).then(
      () => {},
      (err) => setError({ contactId: contact.id, text: err.message }),
    );
  };

  const onAction = (action, contact) => {
    setError(null);
    if (action === ACCESS_ACTIONS.revoke) setRevoking(contact);
    else setPreview({ contact, action });
  };

  return (
    <>
      <div className={s.sectionTitle}>{w('familyAccess')}</div>
      {access.failed ? <div className={s.error}>Could not read the family’s Family Portal access.</div> : null}
      {contacts.length === 0 && !contactsLoading ? (
        <Card><div className={s.help}>{w('noContacts')}</div></Card>
      ) : null}
      {contacts.map((contact) => {
        const record = accessOf(contact);
        const status = familyPortalAccessStatus(record?.status ?? null);
        const hasEmail = Boolean(familyPortalEmailKey(contact.email));
        const actions = rows == null ? [] : familyPortalAccessActions(status, hasEmail);
        return (
          <Card key={contact.id}>
            <div className={s.row}>
              <span className={s.name}>{contactCardName(contact)}</span>
              {rows == null ? null : <StatusPill status={status} />}
              <span className={s.spacer} />
              <span className={s.buttons}>
                {actions.map((a) => (
                  <Button key={a} variant="secondary" size="sm" onClick={() => onAction(a, contact)}>
                    {w(ACTION_LABEL[a])}
                  </Button>
                ))}
              </span>
            </div>
            <div className={s.help}>{w('rowHelp')}</div>
            {status === ACCESS.invited && record?.email ? <div className={s.detail}>{w('sentTo')} {record.email}</div> : null}
            {status === ACCESS.active && record?.email ? <div className={s.detail}>{w('activeAs')} {record.email}</div> : null}
            {status === ACCESS.revoked ? <div className={s.detail}>{w('revokedNote')}</div> : null}
            {status === ACCESS.loginDeleted ? <div className={s.detail}>{w('loginDeletedNote')}</div> : null}
            {!hasEmail && status !== ACCESS.active ? <div className={s.warning}>{w('emailRequired')}</div> : null}
            {error?.contactId === contact.id ? <div className={s.error}>{error.text}</div> : null}
          </Card>
        );
      })}

      <Drawer
        open={Boolean(preview)}
        title={w(PREVIEW_TITLE[preview?.action] || 'previewTitle')}
        onClose={() => { if (!busy) setPreview(null); }}
        width={640}
        footer={(
          <div className={s.footer}>
            <Button variant="secondary" onClick={() => setPreview(null)} disabled={busy}>{w('previewCancel')}</Button>
            <Button onClick={send} loading={busy}>{w('previewSend')}</Button>
          </div>
        )}
      >
        {preview ? (
          <>
            <dl className={s.preview}>
              <dt className={s.previewLabel}>{w('previewFrom')}</dt>
              <dd className={s.previewValue}>
                {familyPortalInviteFrom(studioName, studioEmail)}
              </dd>
              <dt className={s.previewLabel}>{w('previewTo')}</dt>
              <dd className={s.previewValue}>
                {formatAddress({ email: String(preview.contact.email || '').trim(), name: contactCardName(preview.contact) })}
              </dd>
              <dt className={s.previewLabel}>{w('previewSubject')}</dt>
              <dd className={s.previewValue}>{familyPortalInviteSubject()}</dd>
              <dt className={s.previewLabel}>{w('previewMessage')}</dt>
              <dd className={[s.previewValue, s.message].join(' ')}>
                {familyPortalInviteText({
                  firstName: firstWord(preview.contact.firstName),
                  email: String(preview.contact.email || '').trim(),
                  setupUrl: familyPortalSetupPlaceholder(),
                  // The sign-off: the teacher's first name as stored, else the
                  // sign-in's first word (`teacherNameParts`, #797), as the
                  // server's invite signs it (portal/invites.js).
                  teacherName: teacherFirstName,
                  studioName,
                })}
              </dd>
            </dl>
            {error?.preview ? <div className={s.error}>{error.preview}</div> : null}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={Boolean(revoking)}
        title={w('revokeTitle')}
        message={w('revokeBody')}
        confirmLabel={w('revokeConfirm')}
        onConfirm={revoke}
        // A plain confirm: the discard slot is the "no".
        discardLabel={w('previewCancel')}
        onDiscard={() => setRevoking(null)}
        cancelLabel={null}
        onCancel={() => setRevoking(null)}
      />
    </>
  );
}
